package com.havn.app.features.tasks.presentation

import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.domain.HavnTaskDraft
import com.havn.app.features.tasks.domain.HavnTaskPriority
import com.havn.app.features.tasks.domain.HavnTaskRepository
import com.havn.app.features.tasks.domain.HavnTaskStatus
import com.havn.app.features.tasks.domain.HavnTaskType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.cancel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

class TasksViewModel(
    private val taskRepository: HavnTaskRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(TasksUiState())
    val uiState: StateFlow<TasksUiState> = _uiState.asStateFlow()
    private var agentId: String? = null
    private var agencyId: String? = null
    private var actAsAgent = false
    private var hasLoadedAgencyContext = false

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                val nextAgentId = (state as? AuthState.LoggedIn)?.user?.id
                if (nextAgentId == null) {
                    agentId = null
                    hasLoadedAgencyContext = false
                    _uiState.value = TasksUiState()
                } else if (nextAgentId != agentId) {
                    agentId = nextAgentId
                    if (hasLoadedAgencyContext) {
                        loadTaskColumns(nextAgentId, TaskBoardColumn.entries, append = false)
                    }
                }
            }
        }
    }

    fun setAgencyContext(nextAgencyId: String?, nextActAsAgent: Boolean = false) {
        if (hasLoadedAgencyContext && agencyId == nextAgencyId && actAsAgent == nextActAsAgent) return
        agencyId = nextAgencyId
        actAsAgent = nextActAsAgent
        hasLoadedAgencyContext = true
        refresh()
    }

    fun refresh() {
        val currentAgentId = agentId ?: return
        scope.launch { loadTaskColumns(currentAgentId, TaskBoardColumn.entries, append = false) }
    }

    fun loadMoreTasks(column: TaskBoardColumn = _uiState.value.selectedColumn) {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        if (state.isLoading || state.isLoadingMore(column) || !state.hasMoreTasks(column)) return
        scope.launch { loadTaskColumns(currentAgentId, listOf(column), append = true) }
    }

    fun selectTask(taskId: String?) {
        _uiState.update { it.copy(selectedTaskId = taskId) }
    }

    fun updateSearchQuery(query: String) {
        _uiState.update { it.copy(searchQuery = query) }
    }

    fun selectColumn(column: TaskBoardColumn) {
        _uiState.update { it.copy(selectedColumn = column) }
    }

    fun startAddTask() {
        startAddTask(
            title = "",
            description = "",
            propertyId = null,
            contactId = null,
            openHouseId = null,
            leadId = null
        )
    }

    fun startAddTask(
        title: String = "",
        description: String = "",
        propertyId: String? = null,
        contactId: String? = null,
        openHouseId: String? = null,
        leadId: String? = null
    ) {
        _uiState.update {
            it.copy(
                mode = TaskScreenMode.Editor,
                selectedTaskId = null,
                form = TaskFormState(
                    title = title,
                    description = description,
                    status = it.selectedColumn.status,
                    assignedToUserId = agentId,
                    propertyId = propertyId,
                    contactId = contactId,
                    openHouseId = openHouseId,
                    leadId = leadId
                ),
                errorMessage = null
            )
        }
    }

    fun openTask(taskId: String) {
        openTask(taskId, backTarget = null)
    }

    fun openTaskFromToday(taskId: String) {
        openTask(taskId, backTarget = TaskDetailBackTarget.Today)
    }

    private fun openTask(taskId: String, backTarget: TaskDetailBackTarget?) {
        val task = _uiState.value.tasks.firstOrNull { it.id == taskId } ?: return
        _uiState.update {
            it.copy(
                mode = TaskScreenMode.Editor,
                selectedTaskId = taskId,
                selectedColumn = task.status.toBoardColumn(),
                form = TaskFormState.fromTask(task),
                detailBackTarget = backTarget,
                errorMessage = null
            )
        }
    }

    fun closeEditor() {
        _uiState.update {
            it.copy(
                mode = TaskScreenMode.Board,
                form = TaskFormState(),
                detailBackTarget = null,
                errorMessage = null
            )
        }
    }

    fun updateForm(transform: TaskFormState.() -> TaskFormState) {
        _uiState.update { it.copy(form = it.form.transform(), errorMessage = null) }
    }

    fun saveTask() {
        val currentAgentId = agentId ?: return
        val state = _uiState.value
        val title = state.form.title.trim()
        if (title.isBlank()) {
            _uiState.update { it.copy(errorMessage = "Task title is required.") }
            return
        }
        val draft = HavnTaskDraft(
            title = title,
            description = state.form.description.trim().takeIf { it.isNotBlank() },
            type = HavnTaskType.Custom,
            status = state.form.status,
            priority = state.form.priority,
            dueAt = state.form.dueAt,
            agencyId = agencyId,
            assignedToUserId = if (agencyId != null) state.form.assignedToUserId else state.form.assignedToUserId ?: currentAgentId,
            propertyId = state.form.propertyId,
            contactId = state.form.contactId,
            openHouseId = state.form.openHouseId,
            leadId = state.form.leadId
        )
        scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = null) }
            val result = state.selectedTaskId?.let { taskId ->
                taskRepository.updateTask(currentAgentId, taskId, draft)
            } ?: taskRepository.createTask(currentAgentId, draft)
            result.onSuccess { task ->
                _uiState.update {
                    val previousTask = state.selectedTaskId?.let { selectedId ->
                        it.tasks.firstOrNull { existing -> existing.id == selectedId }
                    }
                    it.copy(
                        isSaving = false,
                        mode = TaskScreenMode.Board,
                        form = TaskFormState(),
                        selectedColumn = task.status.toBoardColumn(),
                        tasks = (listOf(task) + it.tasks.filterNot { existing -> existing.id == task.id }).sortedForTaskBoard(),
                        selectedTaskId = task.id,
                        columnCounts = it.columnCounts.adjustForTaskChange(previousTask, task)
                    )
                }
            }.onFailure { error ->
                _uiState.update { it.copy(isSaving = false, errorMessage = error.havnFriendlyMessage("Could not create task.")) }
            }
        }
    }

    fun acceptSuggestedTask(draft: HavnTaskDraft) {
        val currentAgentId = agentId ?: return
        val assignedDraft = draft.copy(assignedToUserId = draft.assignedToUserId ?: currentAgentId)
        scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = null) }
            taskRepository.createTask(currentAgentId, assignedDraft)
                .onSuccess { task ->
                    _uiState.update { state ->
                        state.copy(
                            isSaving = false,
                            tasks = (listOf(task) + state.tasks.filterNot { it.id == task.id }).sortedForTaskBoard(),
                            columnCounts = state.columnCounts.adjustForTaskChange(null, task),
                            dismissedSuggestionIds = state.dismissedSuggestionIds + assignedDraft.suggestionKey()
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(isSaving = false, errorMessage = error.havnFriendlyMessage("Could not save task.")) }
                }
        }
    }

    fun dismissSuggestion(suggestionId: String) {
        _uiState.update { it.copy(dismissedSuggestionIds = it.dismissedSuggestionIds + suggestionId) }
    }

    fun markDone(taskId: String) {
        updateStatus(taskId, HavnTaskStatus.Done)
    }

    fun startProgress(taskId: String) {
        updateStatus(taskId, HavnTaskStatus.InProgress)
    }

    fun reopen(taskId: String) {
        updateStatus(taskId, HavnTaskStatus.Open)
    }

    fun moveToStatus(taskId: String, status: HavnTaskStatus) {
        updateStatus(taskId, status)
    }

    fun dispose() {
        scope.cancel()
    }

    private fun updateStatus(taskId: String, status: HavnTaskStatus) {
        val currentAgentId = agentId ?: return
        scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = null) }
            taskRepository.updateStatus(
                agentId = currentAgentId,
                taskId = taskId,
                status = status,
                completedAt = if (status == HavnTaskStatus.Done) nowIsoLike() else null
            ).onSuccess { updated ->
                _uiState.update { state ->
                    val previousTask = state.tasks.firstOrNull { it.id == updated.id }
                    state.copy(
                        isSaving = false,
                        tasks = state.tasks.map { if (it.id == updated.id) updated else it }.sortedForTaskBoard(),
                        columnCounts = state.columnCounts.adjustForTaskChange(previousTask, updated)
                    )
                }
            }.onFailure { error ->
                _uiState.update { it.copy(isSaving = false, errorMessage = error.havnFriendlyMessage("Could not update task.")) }
            }
        }
    }

    private suspend fun loadTaskColumns(currentAgentId: String, columns: List<TaskBoardColumn>, append: Boolean) {
        val requestedColumns = columns.distinct()
        if (requestedColumns.isEmpty()) return
        _uiState.update {
            if (append) {
                it.copy(
                    loadingMoreColumns = it.loadingMoreColumns + requestedColumns.toSet(),
                    errorMessage = null
                )
            } else {
                it.copy(
                    isLoading = true,
                    loadingColumns = it.loadingColumns + requestedColumns.toSet(),
                    loadingMoreColumns = it.loadingMoreColumns - requestedColumns.toSet(),
                    errorMessage = null
                )
            }
        }
        val loaded = coroutineScope {
            requestedColumns.map { column ->
                async {
                    val offset = if (append) {
                        _uiState.value.tasks.count { it.belongsToColumn(column) }
                    } else {
                        0
                    }
                    column to taskRepository.taskPage(
                        agentId = currentAgentId,
                        agencyId = agencyId,
                        statuses = column.statuses,
                        offset = offset,
                        limit = TASK_PAGE_SIZE,
                        personalScope = actAsAgent
                    )
                }
            }.awaitAll()
        }
        val firstFailure = loaded.firstOrNull { it.second.isFailure }?.second?.exceptionOrNull()
        val successfulPages = loaded.mapNotNull { (column, result) ->
            result.getOrNull()?.let { column to it }
        }
        _uiState.update { state ->
            var nextTasks = state.tasks
            val nextHasMore = state.hasMoreByColumn.toMutableMap()
            val nextColumnCounts = state.columnCounts.toMutableMap()
            successfulPages.forEach { (column, page) ->
                val tasks = page.tasks
                nextTasks = if (append) {
                    (nextTasks + tasks).distinctBy { task -> task.id }.sortedForTaskBoard()
                } else {
                    (tasks + nextTasks.filterNot { task -> task.belongsToColumn(column) }).sortedForTaskBoard()
                }
                nextColumnCounts[column] = page.totalCount
                val loadedThrough = if (append) {
                    state.tasks.count { it.belongsToColumn(column) } + tasks.size
                } else {
                    tasks.size
                }
                nextHasMore[column] = loadedThrough < page.totalCount
            }
            state.copy(
                isLoading = false,
                loadingColumns = state.loadingColumns - requestedColumns.toSet(),
                loadingMoreColumns = state.loadingMoreColumns - requestedColumns.toSet(),
                tasks = nextTasks,
                columnCounts = nextColumnCounts,
                hasMoreByColumn = nextHasMore,
                errorMessage = firstFailure?.havnFriendlyMessage("Could not load tasks. Please try again.") ?: state.errorMessage
            )
        }
    }
}

data class TasksUiState(
    val tasks: List<HavnTask> = emptyList(),
    val mode: TaskScreenMode = TaskScreenMode.Board,
    val selectedTaskId: String? = null,
    val detailBackTarget: TaskDetailBackTarget? = null,
    val searchQuery: String = "",
    val selectedColumn: TaskBoardColumn = TaskBoardColumn.ToDo,
    val form: TaskFormState = TaskFormState(),
    val dismissedSuggestionIds: Set<String> = emptySet(),
    val isLoading: Boolean = false,
    val loadingColumns: Set<TaskBoardColumn> = emptySet(),
    val loadingMoreColumns: Set<TaskBoardColumn> = emptySet(),
    val columnCounts: Map<TaskBoardColumn, Int> = emptyMap(),
    val hasMoreByColumn: Map<TaskBoardColumn, Boolean> = emptyMap(),
    val isSaving: Boolean = false,
    val errorMessage: String? = null
) {
    fun isColumnLoading(column: TaskBoardColumn): Boolean = loadingColumns.contains(column)
    fun isLoadingMore(column: TaskBoardColumn): Boolean = loadingMoreColumns.contains(column)
    fun hasMoreTasks(column: TaskBoardColumn): Boolean = hasMoreByColumn[column] == true
    fun totalCount(column: TaskBoardColumn): Int = columnCounts[column] ?: tasks.count { it.belongsToColumn(column) }
}

enum class TaskScreenMode {
    Board,
    Editor
}

enum class TaskDetailBackTarget {
    Today
}

data class TaskFormState(
    val title: String = "",
    val description: String = "",
    val assignedToUserId: String? = null,
    val priority: HavnTaskPriority = HavnTaskPriority.Medium,
    val status: HavnTaskStatus = HavnTaskStatus.Open,
    val dueAt: String? = null,
    val propertyId: String? = null,
    val contactId: String? = null,
    val openHouseId: String? = null,
    val leadId: String? = null
) {
    companion object {
        fun fromTask(task: HavnTask): TaskFormState = TaskFormState(
            title = task.title,
            description = task.description.orEmpty(),
            assignedToUserId = task.assignedToUserId,
            priority = task.priority,
            status = task.status,
            dueAt = task.dueAt,
            propertyId = task.propertyId,
            contactId = task.contactId,
            openHouseId = task.openHouseId,
            leadId = task.leadId
        )
    }
}

enum class TaskBoardColumn(val label: String, val status: HavnTaskStatus) {
    ToDo("To Do", HavnTaskStatus.Open),
    InProgress("In Progress", HavnTaskStatus.InProgress),
    Done("Done", HavnTaskStatus.Done)
}

val TaskBoardColumn.statuses: List<HavnTaskStatus>
    get() = when (this) {
        TaskBoardColumn.ToDo -> listOf(HavnTaskStatus.Open, HavnTaskStatus.Overdue)
        TaskBoardColumn.InProgress -> listOf(HavnTaskStatus.InProgress)
        TaskBoardColumn.Done -> listOf(HavnTaskStatus.Done)
    }

fun HavnTaskStatus.toBoardColumn(): TaskBoardColumn = when (this) {
    HavnTaskStatus.InProgress -> TaskBoardColumn.InProgress
    HavnTaskStatus.Done -> TaskBoardColumn.Done
    else -> TaskBoardColumn.ToDo
}

private fun HavnTaskDraft.suggestionKey(): String =
    listOf(type.databaseValue, propertyId, openHouseId, leadId, followUpId, title).joinToString(":")

private fun HavnTask.belongsToColumn(column: TaskBoardColumn): Boolean = status in column.statuses

private fun Map<TaskBoardColumn, Int>.adjustForTaskChange(
    previousTask: HavnTask?,
    nextTask: HavnTask
): Map<TaskBoardColumn, Int> {
    val previousColumn = previousTask?.toCountedColumn()
    val nextColumn = nextTask.toCountedColumn()
    if (previousColumn == nextColumn && previousTask != null) return this
    val counts = toMutableMap()
    previousColumn?.let { column ->
        counts[column] = ((counts[column] ?: 0) - 1).coerceAtLeast(0)
    }
    nextColumn?.let { column ->
        counts[column] = (counts[column] ?: 0) + 1
    }
    return counts
}

private fun HavnTask.toCountedColumn(): TaskBoardColumn? =
    if (status == HavnTaskStatus.Cancelled) null else status.toBoardColumn()

private fun List<HavnTask>.sortedForTaskBoard(): List<HavnTask> =
    sortedWith(
        compareByDescending<HavnTask> { it.updatedAt ?: it.createdAt ?: it.dueAt.orEmpty() }
            .thenByDescending { it.createdAt ?: it.dueAt.orEmpty() }
            .thenBy { it.dueAt ?: "9999" }
            .thenBy { it.id }
    )

@OptIn(ExperimentalTime::class)
private fun nowIsoLike(): String = Clock.System.now().toString()

private const val TASK_PAGE_SIZE = 20
